import { TextField } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay"
import dayjs, { Dayjs } from "dayjs"
import { useFormContext } from "react-hook-form"
import { GRID_FORM_CONFIG_T } from "../../../../../common/FormGenerator"
import { ResponsibleAutocomplete } from "./ResponsibleAutocomplete"
import { COLORS } from "../../../../../styles/variables"

export type FiltrationBlockName =
    | "actionPlanName"
    | "actionPlanDates"
    | "actionPlanDescription"
    | "actionPlanResponsible"

type ActionPlanDatePickerProps = {
    value?: string
    onChange?: (value: string) => void
    error?: boolean
    helperText?: string
    placeholder?: string
    minDate?: Dayjs | null
    referenceDate?: Dayjs | null
    highlightedDate?: Dayjs | null
    name?: string
}

const parseFormDateToDayjs = (value?: string): Dayjs | null => {
    const raw = (value ?? "").trim()
    if (!raw) return null
    const [dd, mm, yyyy] = raw.split(".")
    if (!dd || !mm || !yyyy) return null
    const parsed = dayjs(`${yyyy}-${mm}-${dd}`)
    if (!parsed.isValid() || parsed.format("DD.MM.YYYY") !== raw) return null
    return parsed
}

const formatDayjsToFormDate = (value: Dayjs | null): string =>
    value && value.isValid() ? value.format("DD.MM.YYYY") : ""

const ActionPlanDatePicker = ({
    value,
    onChange,
    error,
    helperText,
    placeholder,
    minDate,
    referenceDate,
    highlightedDate,
    name,
}: ActionPlanDatePickerProps) => {
    const { watch } = useFormContext()
    const isDateFromField = typeof name === "string" && name.endsWith(".date_from")
    const isDateToField = typeof name === "string" && name.endsWith(".date_to")
    const stepIndexMatch = typeof name === "string" ? name.match(/^actionPlan\.(\d+)\.(date_from|date_to)$/) : null
    const stepIndex = stepIndexMatch ? Number(stepIndexMatch[1]) : 0

    const dateFromPath =
        isDateToField && typeof name === "string"
            ? name.replace(/\.date_to$/, ".date_from")
            : null
    const watchedDateFrom = dateFromPath ? String(watch(dateFromPath) ?? "") : ""
    const watchedDateFromDayjs = parseFormDateToDayjs(watchedDateFrom)
    const planStartMin =
        isDateFromField && stepIndex > 0
            ? parseFormDateToDayjs(String(watch("actionPlan.0.date_from") ?? ""))
            : null

    const effectiveHighlightedDate = highlightedDate ?? watchedDateFromDayjs
    const effectiveMinDate = isDateToField
        ? (minDate ?? watchedDateFromDayjs)
        : isDateFromField && stepIndex > 0
          ? planStartMin
          : minDate
    const effectiveReferenceDate = referenceDate ?? watchedDateFromDayjs

    return (
        <DatePicker
            format="DD.MM.YYYY"
            value={parseFormDateToDayjs(value)}
            onChange={(newValue) => onChange?.(formatDayjsToFormDate(newValue))}
            minDate={effectiveMinDate ?? undefined}
            referenceDate={effectiveReferenceDate ?? undefined}
            disableHighlightToday
            slots={{
                day: (dayProps: PickersDayProps<Dayjs>) => {
                    const isHighlightedFrom =
                        effectiveHighlightedDate != null &&
                        dayProps.day.isSame(effectiveHighlightedDate, "day") &&
                        !dayProps.selected

                    return (
                        <PickersDay
                            {...dayProps}
                            sx={
                                isHighlightedFrom
                                    ? {
                                          border: `1px solid ${COLORS.CERULEAN}`,
                                          color: COLORS.CERULEAN,
                                          fontWeight: 600,
                                      }
                                    : { '&.MuiPickersDay-today': { border: "none" } }
                            }
                        />
                    )
                },
            }}
            slotProps={{
                textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder,
                    error,
                    helperText,
                    inputProps: { readOnly: true },
                },
            }}
        />
    )
}


export const actionPlanNameConfig = [
    [
        {
            id: 1,
            controllerName: "title",
            RenderElement: TextField,
            renderProps: () => ({
                placeholder: "Название шага должно быть до 60 символов...",
                fullWidth: true,
                size: "small",
                inputProps: { maxLength: 60 },
            }),
        },
    ],
]

export const actionPlanDatesConfig = [
    [
        {
            id: 2,
            controllerName: "date_from",
            RenderElement: ActionPlanDatePicker,
            renderProps: () => ({
                placeholder: "От",
            }),
        },
        {
            id: 5,
            controllerName: "date_to",
            RenderElement: ActionPlanDatePicker,
            renderProps: () => ({
                placeholder: "До",
            }),
        },
    ],
]

export const actionPlanDescriptionConfig = [
    [
        {
            id: 3,
            controllerName: "description",
            RenderElement: TextField,
            renderProps: () => ({
                placeholder: "Введите краткое описание шага",
                fullWidth: true,
                multiline: true,
                size: "small",
            }),
        },
    ],
]



export const actionPlanResponsibleConfig = [
    [
        {
            id: 4,
            controllerName: "responsibles",
            RenderElement: ResponsibleAutocomplete,
            renderProps: (formProps: any) => ({
                fullWidth: true,
                size: "small",
                error: Boolean(formProps.error),
                helperText: formProps.helperText,
            }),
        },
    ],
]

const formConfigs: Record<FiltrationBlockName, GRID_FORM_CONFIG_T<any>> = {
    actionPlanName: actionPlanNameConfig,
    actionPlanDescription: actionPlanDescriptionConfig,
    actionPlanDates: actionPlanDatesConfig,
    actionPlanResponsible: actionPlanResponsibleConfig,
}

export const getFormConfig = (blockName: FiltrationBlockName) => formConfigs[blockName]

export const getDynamicConfig = (blockName: FiltrationBlockName, index: number, arrayName: string): GRID_FORM_CONFIG_T<any> => {
    const config = getFormConfig(blockName);
    
    return [...config.map(row => 
        row.map(field => ({
            ...field,
            controllerName: `${arrayName}.${index}.${field.controllerName}`
        }))
    )];
};
